import React, { useEffect, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Button } from 'reactstrap';

import { ApplicationModel, getApplication } from 'src/redux/actions/applications.action';
import { DynamicFaqModel, PageQueryParams, QueryParams } from 'src/shared/models';
import { List } from 'src/shared/components';

import { getDynamicFaqs } from 'src/redux/actions/dynamic-faq.action';
import DynamicFaqModalForm from './forms/DynamicFaqModalForm';
import { getSignedUrlAndOpenFile } from 'src/shared/services/upload.service';

interface Props {
  getDynamicFaqs: (appId: string, queryParams: PageQueryParams) => void;
  getApplication: Function;
  application: ApplicationModel;
  dynamicFaqs: DynamicFaqModel[];
  numberOfDynamicFaqs: number;
  dynamicFaqsLoading: boolean;
  dynamicFaqsLoaded: boolean;
  dynamicFaqsError: boolean;
}

const DynamicFaq: FC<Props> = ({
  getDynamicFaqs,
  getApplication,
  application,
  dynamicFaqs,
  numberOfDynamicFaqs,
  dynamicFaqsLoading,
  dynamicFaqsLoaded,
  dynamicFaqsError,
}) => {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const [queryParamsDef, setQueryParamsDef] = useState<QueryParams>({
    page: 0,
    size: 20,
    limit: 20,
  });

  useEffect(() => {
    if (!application.id) {
      getApplication(appId);
    }
    /* eslint-disable-next-line */
  }, []);

  const fetchDynamicFaqs = (queryParams: QueryParams) => {
    setQueryParamsDef(queryParams);
    getDynamicFaqs(appId, queryParams);
  };

  const refreshDynamicFaqs = () => {
    getDynamicFaqs(appId, queryParamsDef);
  };

  return (
    <div className="animated fadeIn">
      <List
        useCardContent
        data={dynamicFaqs}
        totalElements={numberOfDynamicFaqs}
        loaded={dynamicFaqsLoaded}
        loading={dynamicFaqsLoading}
        error={dynamicFaqsError}
        fetchData={fetchDynamicFaqs}
        title={t('dynamicFaq.dynamicFaq')}
        createPermission="import_create"
        createModal={(isModalOpen, setIsModalOpen) => (
          <DynamicFaqModalForm
            isOpen={isModalOpen}
            toggleModalState={setIsModalOpen}
            onSaved={refreshDynamicFaqs}
          />
        )}
        fields={[
          { key: 'id', label: t('common.id') },
          { key: 'url', label: t('common.url'), render: item => item.uploadedDocument.url },
          { key: 'updatedAt', label: t('common.updated'), dateTime: true },
          {
            key: 'title',
            label: t('importConfiguration.configuration'),
            render: item => item.configuration.title,
          },
          { key: 'status', label: t('common.status') },
          {
            key: 'download',
            label: t('common.download'),
            render: item => (
              <>
                <Button
                  className="btn-sm float-right"
                  onClick={() => {
                    getSignedUrlAndOpenFile(item.uploadedDocument.url);
                  }}
                >
                  <i className="file-icon far fa-file-alt" />
                </Button>
              </>
            ),
          },
        ]}
        noDataLabel={t('dynamicFaq.noDynamicFaqs')}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    application: state.application.data,
    dynamicFaqs: state.dynamicFaq.data.content,
    numberOfDynamicFaqs: state.dynamicFaq.data.totalElements,
    dynamicFaqsLoading: state.dynamicFaq.loading,
    dynamicFaqsLoaded: state.dynamicFaq.loaded,
    dynamicFaqsError: state.dynamicFaq.error,
  };
};

const mapDispatchToProps = {
  getApplication,
  getDynamicFaqs,
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFaq);
